'use client';
import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XCircle, RefreshCw, WifiOff, ServerCrash, AlertTriangle, X } from 'lucide-react';
import { useErrorModal } from '@/store/useErrorModalStore';
import Icon from '@/components/ui/AppIcon';


const iconMap = {
  network: WifiOff,
  server: ServerCrash,
  auth: AlertTriangle,
  default: XCircle,
};

export default function ErrorModal() {
  const { isOpen, title, message, errorType, retryFn, fallbackMessage, close } = useErrorModal();

  const Icon = iconMap?.[errorType ?? 'default'] ?? XCircle;

  const handleRetry = () => {
    close();
    if (retryFn) {
      setTimeout(() => retryFn(), 150);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          key="error-modal-backdrop"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 flex items-end justify-center p-4"
          style={{ background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(6px)', zIndex: 9990 }}
          onClick={close}
        >
          <motion.div
            key="error-modal-card"
            initial={{ y: 80, opacity: 0, scale: 0.96 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 80, opacity: 0, scale: 0.96 }}
            transition={{ type: 'spring', stiffness: 380, damping: 28 }}
            className="w-full max-w-sm rounded-3xl overflow-hidden"
            style={{ background: 'var(--card)', border: '1px solid var(--border)' }}
            onClick={(e) => e?.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-5 pt-5 pb-0">
              <div
                className="w-11 h-11 rounded-2xl flex items-center justify-center flex-shrink-0"
                style={{ background: 'var(--negative-bg, rgba(239,68,68,0.12))' }}
              >
                <Icon size={22} className="text-negative" style={{ color: 'var(--negative, #ef4444)' }} />
              </div>
              <button
                onClick={close}
                className="w-8 h-8 rounded-full flex items-center justify-center transition-all active:scale-90"
                style={{ background: 'var(--muted)', color: 'var(--muted-foreground)' }}
                aria-label="Close"
              >
                <X size={16} />
              </button>
            </div>

            {/* Body */}
            <div className="px-5 pt-4 pb-2">
              <h3 className="font-bold text-base text-foreground mb-1">
                {title || 'Something went wrong'}
              </h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                {message || 'An unexpected error occurred. Please try again.'}
              </p>
            </div>

            {/* Fallback UI */}
            {fallbackMessage && (
              <div
                className="mx-5 my-3 px-4 py-3 rounded-2xl text-sm"
                style={{
                  background: 'var(--warning-bg, rgba(245,158,11,0.1))',
                  color: 'var(--warning, #f59e0b)',
                  border: '1px solid var(--warning-border, rgba(245,158,11,0.2))',
                }}
              >
                <span className="font-medium">Fallback: </span>
                {fallbackMessage}
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-3 px-5 pt-2 pb-5">
              <button
                onClick={close}
                className="flex-1 py-3 rounded-2xl text-sm font-semibold transition-all active:scale-95"
                style={{
                  background: 'var(--muted)',
                  color: 'var(--muted-foreground)',
                  border: '1px solid var(--border)',
                }}
              >
                Dismiss
              </button>
              {retryFn && (
                <button
                  onClick={handleRetry}
                  className="flex-1 py-3 rounded-2xl text-sm font-bold flex items-center justify-center gap-2 transition-all active:scale-95"
                  style={{
                    background: 'var(--primary, #F59E0B)',
                    color: 'var(--primary-foreground, #000)',
                  }}
                >
                  <RefreshCw size={15} />
                  Retry
                </button>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
